/**
 * Design tokens - the only place a design-system value is written.
 *
 * The palette and metrics come from the frontend design brief, which
 * supersedes the palette and metric rows of 05_Design_System; every other row
 * of that sheet still stands, including WCAG 2.2 AA. Where the two met head-on
 * the resolution is the one the brief itself sets out for semantics: two rows,
 * two bars. A colour used as a MARK - an icon, a dot, a chip fill, a rule, a
 * chart series - is held to 3:1. A colour used for a WORD a person reads is
 * held to 4.5:1, and where the mark value cannot carry a word, a darker
 * sibling does. See docs/DECISIONS.md D-012.
 *
 * check:constants scans the UI and web sources for every hex, rgba, pixel and
 * millisecond value the effective design system names, and fails if one
 * appears anywhere else - or more than once here. Every literal below appears
 * exactly once; anything needing the same value refers to it.
 */

// ---------------------------------------------------------------------------
// Raw literals. One occurrence each, referred to by everything below.
// ---------------------------------------------------------------------------

/** The pixel scale. Nothing off this list is expressible in the product. */
/**
 * The fixed scale.
 *
 * Exported because `check:constants` audits the specification against it: a
 * width named in 04_Screens that is not on this scale is a value from an
 * earlier draft, and catching it in the sheet catches it before somebody types
 * it into a component.
 */
export const px = {
  1: '1px',
  2: '2px',
  3: '3px',
  4: '4px',
  6: '6px',
  8: '8px',
  9: '9px',
  10: '10px',
  '10.5': '10.5px',
  11: '11px',
  '11.5': '11.5px',
  12: '12px',
  '12.5': '12.5px',
  13: '13px',
  '13.5': '13.5px',
  14: '14px',
  16: '16px',
  18: '18px',
  20: '20px',
  24: '24px',
  26: '26px',
  30: '30px',
  31: '31px',
  32: '32px',
  36: '36px',
  40: '40px',
  44: '44px',
  48: '48px',
  56: '56px',
  64: '64px',
  232: '232px',
  320: '320px',
  480: '480px',
  640: '640px',
  768: '768px',
  900: '900px',
  1024: '1024px',
  1280: '1280px',
  96: '96px',
  116: '116px',
  128: '128px',
  192: '192px',
  256: '256px',
  292: '292px',
  384: '384px',
  244: '244px',
  1360: '1360px',
  1440: '1440px',
  1536: '1536px',
  pill: '999px',
} as const;

const hex = {
  // light surfaces
  canvasLight: '#F6F4F0',
  white: '#FFFDFB',
  sunkenLight: '#EFEBE4',
  // light ink
  ink900Light: '#221F1A',
  ink600Light: '#554E44',
  ink400Light: '#8A8175',
  ink300Light: '#9A9184',
  ink200Light: '#C4BCAE',
  // ink that carries words at 4.5:1 where the mark value cannot
  inkMetaText: '#6F675A',
  inkLabelText: '#6E685E',
  // bronze
  bronze: '#8A6A43',
  bronzeHover: '#75593A',
  bronzeTintLight: '#EFE8DD',
  /*
   * Soft backgrounds for the semantic chips.
   *
   * These were named in ~120 places across the application - `bg-warning-tint`,
   * `bg-danger-tint` - and defined nowhere, so every warning and danger chip in
   * the product rendered with no background at all. Tailwind emits nothing for
   * a utility it does not know and says nothing about it, and the contrast
   * check measures the palette rather than the screen, so both were quiet.
   *
   * Opaque in the light theme, matching bronze's tint: a flat wash reads the
   * same over a panel and over the canvas, and a chip that changes shade
   * depending on what is behind it looks like a rendering fault.
   */
  successTintLight: '#E4F2EA',
  warningTintLight: '#F6EDDC',
  dangerTintLight: '#F8E7E2',
  infoTintLight: '#E3EEF5',
  bronzeText: '#7F623E',
  // semantic marks: dots, bars, chips, strokes. 3:1.
  markSuccess: '#2E8B5F',
  markWarning: '#A87928',
  markDanger: '#C25A42',
  markInfo: '#3E7FA6',
  // semantic words on a light surface. 4.5:1.
  textSuccessLight: '#2C6B4F',
  textWarningLight: '#8A5A12',
  textDangerLight: '#93412E',
  textInfoLight: '#2A5C7A',
  /** The hero plate on a light page. Not the dark theme's canvas. */
  plateDark: '#262219',
  // dark surfaces
  canvasDark: '#1C1A17',
  surfaceDark: '#24211D',
  sunkenDark: '#191713',
  // dark ink
  ink900Dark: '#EDE8E0',
  ink600Dark: '#A79E90',
  // dark bronze. Hover goes lighter, not darker, and the dark theme's button
  // label is the dark canvas colour - white on the dark accent is only 3.08:1.
  bronzeDark: '#B08D5E',
  bronzeDarkHover: '#C6A67C',
  bronzeDarkText: '#BE9C6E',
  // Semantic marks on a dark surface. The light marks come close but two of
  // them fall under 3:1 once a selected row's bronze tint lightens the
  // background beneath them, so dark carries its own row.
  markSuccessDark: '#35A06E',
  markDangerDark: '#CE6650',
  markInfoDark: '#4A8DB8',
  // Semantic words on a dark surface. The light values do not carry at all:
  // they land between 2.2:1 and 3.0:1 there.
  textSuccessDark: '#3EB47D',
  textWarningDark: '#CB9333',
  textDangerDark: '#F0785E',
  textInfoDark: '#55A7D8',
} as const;

const alpha = {
  ruleLight: 'rgba(140,109,70,0.13)',
  ruleStrongLight: 'rgba(140,109,70,0.22)',
  /** Underline on inline actions; also the focus ring in light. */
  bronzeRule: 'rgba(140,109,70,0.40)',
  ruleDark: 'rgba(176,141,94,0.18)',
  ruleStrongDark: 'rgba(176,141,94,0.28)',
  bronzeTintDark: 'rgba(176,141,94,0.14)',
  // Alpha in the dark theme, like bronze's: a solid tint on a dark surface
  // reads as a lighter panel rather than as a coloured chip.
  successTintDark: 'rgba(53,160,110,0.16)',
  warningTintDark: 'rgba(175,126,42,0.18)',
  dangerTintDark: 'rgba(206,102,80,0.18)',
  infoTintDark: 'rgba(74,141,184,0.18)',
  bronzeRuleDark: 'rgba(176,141,94,0.40)',
  shadowPopoverLight: 'rgba(46,42,36,0.10)',
  shadowModalLight: 'rgba(46,42,36,0.16)',
  shadowPopoverDark: 'rgba(0,0,0,0.44)',
  shadowModalDark: 'rgba(0,0,0,0.58)',
} as const;

const duration = {
  state: '150ms',
  overlay: '250ms',
} as const;

// ---------------------------------------------------------------------------
// Type
// ---------------------------------------------------------------------------

export const typography = {
  /**
   * Two Latin families and an Arabic one.
   *
   * The sans is the interface. The serif is a role rather than a moment: it
   * sets figures, page titles and display numerals, which is what gives 2a its
   * character. What it never does is carry a sentence - running text stays in
   * the sans, and that single rule is the whole difference between a display
   * face and a second body face. check:screens enforces it directly.
   *
   * Both stacks resolve locally: a product that needs a font server to look
   * right does not look right on first paint, or offline.
   */
  fontFamily: {
    sans: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    serif:
      '"Instrument Serif", "Iowan Old Style", "Palatino Linotype", "Source Serif 4", Palatino, Georgia, ui-serif, serif',
    /**
     * Identifiers, API names and pasted data.
     *
     * A column key or an API name is read character by character, and a
     * proportional face makes `rn` and `m` the same shape. It was already
     * being asked for as `font-mono` in two screens, against a family the
     * preset had never defined.
     */
    mono: 'ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
    /** Paired to the sans at matching optical weight for Arabic. */
    arabic: '"Noto Naskh Arabic", "Geeza Pro", "Al Bayan", "Segoe UI", "Times New Roman", serif',
  },
  /**
   * 10.5 / 11.5 / 12.5 / 13.5 / 16 / 20 / 26 / 31 / 40. Nothing else.
   *
   * The redesign reference draws twenty-two sizes between 9.5 and 40px. Most
   * of those are one decision written twice - 26 beside 27, 30 beside 31
   * beside 33 beside 34, 13 beside 13.5 beside 14 - which is the failure this
   * repository already records against the six panel widths in 04_Screens.
   * Nine remain, each with a job: a title and a record's name are one size, a
   * hero's second figure and its heading are one size, and body absorbs the
   * 13/13.5/14 cluster.
   */
  size: {
    /** Uppercase labels and eyebrows. The only size below 11. */
    micro: px['10.5'],
    /** Counts beside a nav item, text inside a pill. */
    meta: px['11.5'],
    /** Chips, the second line of a row, a stepper's segments. */
    dense: px['12.5'],
    body: px['13.5'],
    /** A currency prefix set smaller beside the figure it belongs to. */
    lead: px[16],
    h3: px[20],
    h2: px[26],
    h1: px[31],
    /** The one figure a page is about. */
    display: px[40],
  },
  lineHeight: {
    body: '1.45',
    heading: '1.25',
    dense: '1.35',
  },
  /** Two weights. No 600, no 700, no italic in interface chrome. */
  weight: {
    regular: '400',
    medium: '500',
  },
  letterSpacing: {
    body: '0',
    /** Uppercase labels only, at `micro` and `label`. */
    label: '0.06em',
    /** The kicker above a plate header's title. Wider still, and rarer. */
    kicker: '0.26em',
  },
  /**
   * Every number a person reads is tabular: currency, counts, dates, times,
   * percentages, ids. Misaligned digits are what makes a finance screen look
   * untrustworthy, and the fix cannot be per-component discipline.
   */
  numericFeatures: '"tnum" 1, "lnum" 1',
} as const;

// ---------------------------------------------------------------------------
// Space, shape, elevation
// ---------------------------------------------------------------------------

/**
 * Twelve values. Every gap in the product is one of them.
 *
 * Seven could not set 2a, whose rhythm runs finer at the small end: the 6px
 * gap inside a figure block, the 10px between a label and the number under it,
 * the 14px between the marks in a row. The five values added are all below
 * 20px, where two pixels are legible; nothing was added above it, where they
 * are not.
 *
 * The five arrive as half-steps rather than by renumbering, because the whole
 * numbers are already written into six hundred class names: renumbering would
 * have moved every `p-5` in the product from 24px to 12px silently, and a
 * redesign that quietly halves a hundred paddings is not a redesign, it is a
 * regression with a changelog.
 */
export const space = {
  '0.5': px[2],
  1: px[4],
  '1.5': px[6],
  2: px[8],
  '2.5': px[10],
  3: px[12],
  '3.5': px[14],
  4: px[16],
  '4.5': px[20],
  5: px[24],
  6: px[32],
  '6.5': px[40],
  7: px[48],
} as const;

export const radius = {
  /** Progress tracks and funnel rails. */
  rail: px[3],
  control: px[8],
  card: px[10],
  /** The outer plate a page sits on. */
  panel: px[12],
  chip: px.pill,
} as const;

/** One weight. The colour follows the job; see the palette below. */
export const border = {
  width: px[1],
} as const;

/**
 * Three levels, and the first one is nothing.
 *
 * Panels sit flat on the canvas - hierarchy comes from space and type weight,
 * not from outlining every box. Only what floats above the page casts a
 * shadow.
 */
export const elevation = {
  panel: 'none',
  popover: `0 ${px[2]} ${px[8]} ${alpha.shadowPopoverLight}`,
  modal: `0 ${px[8]} ${px[32]} ${alpha.shadowModalLight}`,
} as const;

export const elevationDark = {
  panel: 'none',
  popover: `0 ${px[2]} ${px[8]} ${alpha.shadowPopoverDark}`,
  modal: `0 ${px[8]} ${px[32]} ${alpha.shadowModalDark}`,
} as const;

// ---------------------------------------------------------------------------
// Palette
// ---------------------------------------------------------------------------

export interface Palette {
  canvas: string;
  surface: string;
  sunken: string;
  /**
   * The hero plate.
   *
   * An island of dark on a light page rather than a theme, so it is the same
   * value in both palettes: a plate that inverted with the theme would stop
   * being a plate in dark mode and become an invisible rectangle.
   */
  plate: string;
  rule: string;
  ruleStrong: string;
  ink: {
    /** Primary reading colour. */
    900: string;
    /** Secondary. */
    600: string;
    /** Tertiary, as a MARK: icon strokes, hairlines, glyphs. */
    400: string;
    /** As a MARK: placeholder glyphs, decorative label rules. */
    300: string;
    /** Disabled only. Never information. */
    200: string;
    /** Metadata a person reads. */
    meta: string;
    /** 11px uppercase labels a person reads. */
    label: string;
  };
  bronze: {
    /** Fills, active nav, chip backgrounds, focus. */
    base: string;
    hover: string;
    tint: string;
    /** Underline under an inline action. */
    rule: string;
    /** Bronze as a word: a link, an inline action's label. */
    text: string;
    /** What is legible on top of a bronze fill. */
    on: string;
  };
  /** Marks: dots, bars, chip fills, chart series, strokes. Held to 3:1. */
  mark: { success: string; warning: string; danger: string; info: string };
  /**
   * Soft chip backgrounds. Decorative, so not measured - the word sitting on
   * them is what carries the meaning, and it is held to 4.5:1 against them.
   */
  semanticTint: { success: string; warning: string; danger: string; info: string };
  /** Words a person reads. Held to 4.5:1 on this theme's surfaces. */
  semanticText: { success: string; warning: string; danger: string; info: string };
  borderControl: string;
  /**
   * The focus indicator itself: a solid ring, held to 3:1 like any other mark,
   * because a focus indicator nobody can see is not one (WCAG 2.2).
   */
  focusRing: string;
  /**
   * The soft halo drawn outside the indicator. Decorative, so it is not
   * measured - the brief's accent-at-40% focus value is this halo, and
   * separating the two is what lets the ring be both soft and visible.
   */
  focusHalo: string;
  elevation: { panel: string; popover: string; modal: string };
}

export const light: Palette = {
  canvas: hex.canvasLight,
  surface: hex.white,
  sunken: hex.sunkenLight,
  plate: hex.plateDark,
  rule: alpha.ruleLight,
  ruleStrong: alpha.ruleStrongLight,
  ink: {
    900: hex.ink900Light,
    600: hex.ink600Light,
    400: hex.ink400Light,
    300: hex.ink300Light,
    200: hex.ink200Light,
    meta: hex.inkMetaText,
    label: hex.inkLabelText,
  },
  bronze: {
    base: hex.bronze,
    hover: hex.bronzeHover,
    tint: hex.bronzeTintLight,
    rule: alpha.bronzeRule,
    text: hex.bronzeText,
    on: hex.white,
  },
  mark: {
    success: hex.markSuccess,
    warning: hex.markWarning,
    danger: hex.markDanger,
    info: hex.markInfo,
  },
  semanticTint: {
    success: hex.successTintLight,
    warning: hex.warningTintLight,
    danger: hex.dangerTintLight,
    info: hex.infoTintLight,
  },
  semanticText: {
    success: hex.textSuccessLight,
    warning: hex.textWarningLight,
    danger: hex.textDangerLight,
    info: hex.textInfoLight,
  },
  /** Anything whose edge IS a control: input, select, checkbox, outline button. */
  borderControl: hex.ink400Light,
  focusRing: hex.bronze,
  focusHalo: alpha.bronzeRule,
  elevation,
};

export const dark: Palette = {
  canvas: hex.canvasDark,
  surface: hex.surfaceDark,
  sunken: hex.sunkenDark,
  plate: hex.plateDark,
  rule: alpha.ruleDark,
  ruleStrong: alpha.ruleStrongDark,
  ink: {
    900: hex.ink900Dark,
    600: hex.ink600Dark,
    400: hex.ink400Light,
    300: hex.ink600Dark,
    200: hex.ink400Light,
    meta: hex.ink600Dark,
    label: hex.ink600Dark,
  },
  bronze: {
    base: hex.bronzeDark,
    hover: hex.bronzeDarkHover,
    tint: alpha.bronzeTintDark,
    rule: alpha.bronzeRuleDark,
    text: hex.bronzeDarkText,
    // White on the dark bronze fill is 3.08:1 - not enough for a button label.
    // The dark canvas colour on it is 5.64:1, so a dark bronze button carries
    // dark text. Verified by the contrast check rather than assumed.
    on: hex.canvasDark,
  },
  mark: {
    success: hex.markSuccessDark,
    // The warning mark is the one value that carries across both themes.
    warning: hex.markWarning,
    danger: hex.markDangerDark,
    info: hex.markInfoDark,
  },
  semanticTint: {
    success: alpha.successTintDark,
    warning: alpha.warningTintDark,
    danger: alpha.dangerTintDark,
    info: alpha.infoTintDark,
  },
  semanticText: {
    success: hex.textSuccessDark,
    warning: hex.textWarningDark,
    danger: hex.textDangerDark,
    info: hex.textInfoDark,
  },
  borderControl: hex.ink400Light,
  focusRing: hex.bronzeDark,
  focusHalo: alpha.bronzeRuleDark,
  elevation: elevationDark,
};

export const themes = { light, dark } as const;
export type ThemeName = keyof typeof themes;

// ---------------------------------------------------------------------------
// Layout and component metrics
// ---------------------------------------------------------------------------

export const layout = {
  gridColumns: 12,
  gutter: px[24],
  maxContentWidth: px[1440],
  /** A single centred column: My Work, public surfaces, the proposal queue. */
  readingWidth: px[900],
  navRail: { expanded: px[244], collapsed: px[64] },
  topBar: px[56],
  recordSplit: { timeline: '65%', details: '35%' },
} as const;

/**
 * The two panel widths.
 *
 * 04_Screens named six between 200 and 360 pixels, which is one decision
 * written down six times slightly differently. The design system has one
 * narrow panel and one wide one; docs/design-overrides.json records which
 * sheet value resolves to which, and check:constants fails on a seventh.
 */
export const panel = {
  narrow: px[232],
  wide: px[320],
} as const;

export const breakpoint = {
  sm: px[640],
  md: px[768],
  lg: px[1024],
  xl: px[1280],
  '2xl': px[1536],
} as const;

/** Below this, tables become card lists. */
export const TABLE_TO_CARDS_BREAKPOINT = breakpoint.md;
/** Below this, the details panel drops under the timeline. */
export const RECORD_SPLIT_BREAKPOINT = breakpoint.xl;

/**
 * Row column widths.
 *
 * A row of facts aligns down a card because its columns are declared, not
 * because each cell happened to be a similar length. `space-between` on a
 * 1,360px card puts eighteen hundred pixels between a label and its value,
 * which is the defect these exist to remove.
 */
export const rowColumns = {
  /** A kind or a type, with an icon before it. */
  kind: px[128],
  /** A timing, right-aligned. */
  when: px[96],
  /** An action that appears on hover. */
  act: px[64],
  /** A count, right-aligned and narrow. */
  count: px[32],
  /** A money column. */
  amount: px[116],
} as const;

export const control = {
  table: {
    rowComfortable: px[44],
    rowCompact: px[36],
    header: px[40],
  },
  input: { height: px[36], paddingX: px[12] },
  button: {
    heightSmall: px[32],
    height: px[36],
    paddingXSmall: px[12],
    paddingX: px[16],
  },
  chip: { height: px[24], paddingX: px[8] },
  /** A board stage. Five fit across before anybody scrolls. */
  boardColumn: px[292],
  /** A monogram in a presence stack. */
  avatar: px[30],
  slideOver: px[480],
  modal: { default: px[640], confirm: px[480] },
  popoverMax: px[320],
} as const;

/**
 * Block dimensions.
 *
 * The height of a media pane, a builder canvas, or the skeleton standing in
 * for one. Not a gap: the spacing scale governs the distance BETWEEN things,
 * and conflating the two is how `h-48` came to be written in nine places -
 * a value from Tailwind's default scale, which this preset deletes, so every
 * one of those skeletons rendered with no height at all. The design system
 * asks for "skeletons matching the final layout"; they were matching nothing.
 */
export const block = {
  xs: px[64],
  sm: px[96],
  md: px[128],
  lg: px[192],
  xl: px[256],
  '2xl': px[384],
} as const;

/**
 * Chart geometry.
 *
 * A plot area is a design decision like any other, and it was about to be
 * written as `h-48` - a utility the preset deliberately does not define,
 * because the default scale was deleted so off-scale values are unavailable
 * rather than merely discouraged. The bar's own height stays an inline
 * percentage: that one is continuous data, not a scale choice.
 */
export const chart = {
  /*
   * The same 232 as the narrow panel, deliberately.
   *
   * A plot area 200 tall was the first instinct and it would have been an
   * eighth decision about a dimension the system has already settled once -
   * and 200px is exactly the value docs/design-overrides.json already resolves
   * to 232 for SC-18's palette. One narrow dimension, used for a width there
   * and a height here.
   */
  plotHeight: px[232],
  /** Narrow enough that seven steps fit, wide enough for a money figure. */
  columnMin: px[64],
} as const;

/** Icon buttons are the usual offenders, so the minimum is a token. */
export const TOUCH_TARGET_MIN = px[44];

/**
 * A token as a number, for the few places that need arithmetic on one.
 *
 * Virtualisation multiplies a row height by a row count; a media query is a
 * string. Both need the same value, and deriving the number from the token
 * keeps it one value rather than a token and a constant that agree until they
 * do not.
 */
export function pxValue(token: string): number {
  const parsed = Number.parseFloat(token);
  if (Number.isNaN(parsed)) throw new Error(`${token} is not a pixel token.`);
  return parsed;
}

/**
 * Phosphor, light weight.
 *
 * Three sizes with distinct jobs rather than two: 18px is the working size in
 * the nav rail and toolbars, 16px sits inside a line of text, and 20px is
 * reserved for the two places an icon is the subject rather than the label -
 * an empty state and a page header.
 *
 * There is no stroke width here any more. Phosphor's weight IS the stroke -
 * light draws at 1.25px at 18px - and overriding it produces a glyph that is
 * neither this weight nor another. Two weights read as two icon sets, which is
 * why the weight is a token rather than a prop.
 */
export const icon = {
  default: px[18],
  inline: px[16],
  feature: px[20],
  weight: 'light',
} as const;

export const focusRing = {
  width: px[2],
  offset: px[2],
} as const;

export const motion = {
  state: duration.state,
  overlay: duration.overlay,
} as const;

export const toast = {
  standardMs: 6000,
  /** Long enough that an undo can actually be clicked. */
  undoMs: 10000,
} as const;

/**
 * The field budget.
 *
 * 05_Design_System calls this "a product rule enforced in review". Review is
 * not a mechanism, so DetailsPanel enforces it: in Simple mode it renders this
 * many fields and puts the rest behind "Show all". The number lives here so
 * the component and the layout builder's counter cannot disagree.
 */
export const FIELD_BUDGET_SIMPLE = 8;

/** Maximum items on the nav rail (UX-01). The rest go to More. */
export const NAV_RAIL_MAX_ITEMS = 7;

/** Maximum bottom tabs on mobile. */
export const MOBILE_TAB_MAX_ITEMS = 5;

export const tokens = {
  typography,
  space,
  block,
  rowColumns,
  radius,
  border,
  elevation,
  elevationDark,
  themes,
  layout,
  breakpoint,
  control,
  icon,
  focusRing,
  motion,
  toast,
  TOUCH_TARGET_MIN,
  FIELD_BUDGET_SIMPLE,
  NAV_RAIL_MAX_ITEMS,
  MOBILE_TAB_MAX_ITEMS,
} as const;

export type Tokens = typeof tokens;
